import React, { useState, useEffect } from "react";
import {
  Sliders,
  MessageSquare,
  Palette,
  Zap,
  Check,
  Save,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const DEFAULT_CONFIG = {
  systemPrompt:
    "You are a helpful knowledge base assistant. Answer questions based on the provided documents. If you don't know the answer, say so honestly. Always cite your sources.",
  topK: 5,
  similarityThreshold: 0.7,
  reranking: false,
  tone: "professional",
  maxResponseLength: "medium",
  citationStyle: "inline",
  temperature: 0.3,
};

const CONFIG_KEY = (fid) => `hyve-admin-prompt-config-${fid}`;

const loadConfig = (fid, facilitySystemPrompt) => {
  try {
    const stored = JSON.parse(localStorage.getItem(CONFIG_KEY(fid)) || "null");
    return {
      ...DEFAULT_CONFIG,
      // Use the facility's actual system_prompt as the base; localStorage overrides if present
      systemPrompt: facilitySystemPrompt || DEFAULT_CONFIG.systemPrompt,
      ...(stored || {}),
    };
  } catch {
    return {
      ...DEFAULT_CONFIG,
      systemPrompt: facilitySystemPrompt || DEFAULT_CONFIG.systemPrompt,
    };
  }
};

const saveConfig = (fid, cfg) =>
  localStorage.setItem(CONFIG_KEY(fid), JSON.stringify(cfg));

const parseSources = (data) => {
  const raw =
    data.sourceDocuments ||
    data.sources ||
    data.documents ||
    data.context ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 5).map((s) => ({
    name:
      s.metadata?.source || s.metadata?.filename || s.title || s.name || null,
    score: s.score ?? s.similarity ?? null,
  }));
};

const SliderField = ({ label, desc, value, min, max, step, onChange }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
        {value}
      </span>
    </div>
    {desc && <p className="text-xs text-slate-500 mb-2">{desc}</p>}
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-blue-600"
    />
    <div className="flex justify-between text-xs text-slate-400 mt-0.5">
      <span>{min}</span>
      <span>{max}</span>
    </div>
  </div>
);

const PromptStudio = () => {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [saved, setSaved] = useState(false);
  const [testPrompt, setTestPrompt] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetch(`${AUTH_URL}/facilities`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFacilities(list);
        if (list.length > 0) {
          const fac = list[0];
          setFacilityId(fac.facility_id);
          setConfig(loadConfig(fac.facility_id, fac.system_prompt));
        }
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFacilityChange = (fid) => {
    const fac = facilities.find((f) => f.facility_id === fid);
    setFacilityId(fid);
    setConfig(loadConfig(fid, fac?.system_prompt));
    setTestResult(null);
    setSaved(false);
  };

  const update = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!facilityId) return;
    saveConfig(facilityId, config);
    // Persist system_prompt to the DB so it survives cache clears
    try {
      await fetch(`${AUTH_URL}/admin/rag/service-config`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          system_prompt: config.systemPrompt,
        }),
      });
    } catch {
      // Fire-and-forget — localStorage save already succeeded
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const runTest = async () => {
    if (!testPrompt.trim() || testing || !facilityId) return;
    setTesting(true);
    setTestResult(null);
    const start = Date.now();
    try {
      const res = await fetch(`${AUTH_URL}/rag/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          chatInput: testPrompt,
          sessionId: `prompt-studio-${Date.now()}`,
          promptConfig: config,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const sources = parseSources(data);
      setTestResult({
        output:
          data.output || data.text || data.response || JSON.stringify(data),
        sources,
        elapsed: Date.now() - start,
        success: true,
      });
    } catch (err) {
      setTestResult({
        output: err.message,
        sources: [],
        elapsed: Date.now() - start,
        success: false,
      });
    }
    setTesting(false);
  };

  const selectedFacility = facilities.find((f) => f.facility_id === facilityId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            Prompt Engineering Studio
          </h2>
          <p className="text-sm text-slate-500">
            Fine-tune RAG retrieval and response behaviour per facility
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!facilityId}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
            saved
              ? "bg-green-600 text-white"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {saved ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Saved
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </>
          )}
        </button>
      </div>

      {/* Facility selector */}
      <div className="max-w-xs">
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Facility
        </label>
        <select
          value={facilityId}
          onChange={(e) => handleFacilityChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select facility...</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>
              {f.facility_name || f.facility_id}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: System prompt + retrieval */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900 mb-1 flex items-center">
              <MessageSquare className="w-4 h-4 mr-2 text-blue-500" />
              System Prompt
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Instructions given to the AI before each conversation.
            </p>
            <textarea
              value={config.systemPrompt}
              onChange={(e) => update("systemPrompt", e.target.value)}
              rows={8}
              className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono resize-none"
              placeholder="You are a helpful assistant..."
            />
            <div className="flex justify-between mt-2">
              <span className="text-xs text-slate-400">
                {config.systemPrompt.length} characters
              </span>
              <button
                onClick={() =>
                  update("systemPrompt", DEFAULT_CONFIG.systemPrompt)
                }
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Reset to default
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900 mb-1 flex items-center">
              <Sliders className="w-4 h-4 mr-2 text-purple-500" />
              Retrieval Parameters
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Controls how documents are searched and selected for context.
            </p>
            <div className="space-y-5">
              <SliderField
                label="Top K Results"
                desc="Number of document chunks to retrieve per query"
                value={config.topK}
                min={1}
                max={20}
                step={1}
                onChange={(v) => update("topK", v)}
              />
              <SliderField
                label="Similarity Threshold"
                desc="Minimum relevance score (0 = everything, 1 = exact match only)"
                value={config.similarityThreshold}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => update("similarityThreshold", v)}
              />
              <SliderField
                label="Temperature"
                desc="Lower = more focused, higher = more creative"
                value={config.temperature}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => update("temperature", v)}
              />
              <div className="flex items-center justify-between py-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Reranking
                  </label>
                  <p className="text-xs text-slate-500">
                    Re-score results for better accuracy
                  </p>
                </div>
                <button
                  onClick={() => update("reranking", !config.reranking)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    config.reranking ? "bg-blue-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      config.reranking ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Response formatting + test */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900 mb-1 flex items-center">
              <Palette className="w-4 h-4 mr-2 text-emerald-500" />
              Response Formatting
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Controls the style and format of AI responses.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Tone
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      id: "professional",
                      label: "Professional",
                      desc: "Formal, precise",
                    },
                    {
                      id: "friendly",
                      label: "Friendly",
                      desc: "Conversational",
                    },
                    {
                      id: "technical",
                      label: "Technical",
                      desc: "Detailed, exact",
                    },
                    { id: "concise", label: "Concise", desc: "Brief, direct" },
                    {
                      id: "educational",
                      label: "Educational",
                      desc: "Explanatory",
                    },
                    {
                      id: "executive",
                      label: "Executive",
                      desc: "High-level summary",
                    },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => update("tone", t.id)}
                      className={`px-3 py-2 rounded-lg border text-left transition-colors ${
                        config.tone === t.id
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-xs font-medium block">
                        {t.label}
                      </span>
                      <span className="text-xs text-slate-500">{t.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Response Length
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "short", label: "Short", desc: "1-2 sentences" },
                    { id: "medium", label: "Medium", desc: "1-2 paragraphs" },
                    { id: "detailed", label: "Detailed", desc: "Full depth" },
                  ].map((l) => (
                    <button
                      key={l.id}
                      onClick={() => update("maxResponseLength", l.id)}
                      className={`px-3 py-2 rounded-lg border text-center transition-colors ${
                        config.maxResponseLength === l.id
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-xs font-medium block">
                        {l.label}
                      </span>
                      <span className="text-xs text-slate-500">{l.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Citation Style
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      id: "inline",
                      label: "Inline",
                      desc: "Citations in text",
                    },
                    {
                      id: "footnote",
                      label: "Footnote",
                      desc: "References at end",
                    },
                    { id: "none", label: "None", desc: "No citations" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      onClick={() => update("citationStyle", c.id)}
                      className={`px-3 py-2 rounded-lg border text-center transition-colors ${
                        config.citationStyle === c.id
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-xs font-medium block">
                        {c.label}
                      </span>
                      <span className="text-xs text-slate-500">{c.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Test prompt */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900 mb-1 flex items-center">
              <Zap className="w-4 h-4 mr-2 text-amber-500" />
              Test Prompt
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Try a query with the current configuration.
            </p>
            <div className="flex gap-2 mb-3">
              <input
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runTest()}
                placeholder="Enter a test question..."
                disabled={!facilityId}
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
              />
              <button
                onClick={runTest}
                disabled={testing || !testPrompt.trim() || !facilityId}
                className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:bg-slate-300 flex items-center"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-1" />
                    Test
                  </>
                )}
              </button>
            </div>
            {testResult && (
              <div
                className={`rounded-lg border p-4 ${
                  testResult.success
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-xs font-medium ${testResult.success ? "text-green-700" : "text-red-700"}`}
                  >
                    {testResult.success ? "Response" : "Error"}
                  </span>
                  <span className="text-xs text-slate-500">
                    {testResult.elapsed}ms
                    {testResult.sources.length > 0 &&
                      ` · ${testResult.sources.length} sources`}
                  </span>
                </div>
                <p
                  className={`text-sm leading-relaxed whitespace-pre-wrap ${
                    testResult.success ? "text-green-900" : "text-red-900"
                  }`}
                >
                  {testResult.output}
                </p>
                {testResult.sources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs font-medium text-green-700 mb-1">
                      Sources:
                    </p>
                    {testResult.sources.map((s, i) => (
                      <p key={i} className="text-xs text-green-600">
                        {s.name || `Document ${i + 1}`}
                        {s.score != null && ` (${(s.score * 100).toFixed(0)}%)`}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Config summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
          Active Configuration
          {selectedFacility && (
            <span className="ml-2 text-blue-600 font-normal">
              — {selectedFacility.facility_name || facilityId}
            </span>
          )}
        </h4>
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Top-K", value: config.topK },
            {
              label: "Threshold",
              value: config.similarityThreshold.toFixed(2),
            },
            { label: "Temperature", value: config.temperature.toFixed(2) },
            { label: "Reranking", value: config.reranking ? "On" : "Off" },
            { label: "Tone", value: config.tone },
            { label: "Length", value: config.maxResponseLength },
            { label: "Citations", value: config.citationStyle },
          ].map((item) => (
            <span
              key={item.label}
              className="inline-flex items-center px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs"
            >
              <span className="text-slate-500 mr-1">{item.label}:</span>
              <span className="font-medium text-slate-800">{item.value}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromptStudio;
